<!-- First project (#projectFirstModal, z-93: above the start-up profile choice): a profile without projects must create
     one, and everything it already holds goes into it. Name (required, Enter creates) and colour. It cannot be closed,
     and while it is open every global shortcut is blocked (src/features/keyboard.ts). Created only once even with Enter + click or a
     double tap. Shown by the projects store's ensureFirst() (the picker store's pick and switchTo,
     applyLoadedFile), which resolves when the project exists; logic in src/features/projects.ts (createFirstProject). -->
<script lang="ts">
import { SERIES_COLORS } from '../../utils/color'
// The colour chosen last time stays chosen when the dialog comes back (another profile), like before
let lastColor = SERIES_COLORS[0]
</script>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import CardDialog from '../CardDialog.vue'
import ColorSwatches from '../fields/ColorSwatches.vue'
import { useI18n } from '../../composables/useI18n'
import { useDataStore } from '../../stores/data'
import { useProjectsStore } from '../../stores/projects'
import { PROJECT_NAME_MAX, createFirstProject } from '../../features/projects'
import { PROJECT_LISTS } from '../../data/items'
import { saveAppData } from '../../features/storage/save'

const { t } = useI18n()
const store = useDataStore()
const projects = useProjectsStore()

// The profile already holds something (data from before projects existed): it will go into the new project
const d0 = store.getData()
const has = PROJECT_LISTS.some(k => d0[k].length)

const name = ref('')
const color = ref(lastColor)
watch(color, c => { lastColor = c })
const missing = ref(false)
const nameInput = ref<HTMLInputElement | null>(null)
onMounted(() => nextTick(() => nameInput.value?.focus()))
const inputClass = computed(() => ['w-full bg-darkinput border rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:border-brand', missing.value ? 'border-brand' : 'border-gray-800'])

function create() {
  if (!name.value.trim()) { nameInput.value?.focus(); missing.value = true; return }
  const d = store.getData()
  // Only once: Enter + click or a double tap must not create two identical projects
  if (createFirstProject(d, name.value, color.value)) saveAppData(d)
  projects.firstDone()
}
</script>

<template>
  <CardDialog id="projectFirstModal" name="firstProject" z="z-[93]" dim="bg-black/80">
    <span class="w-12 h-12 rounded-2xl bg-brand/15 text-brand flex items-center justify-center text-xl"><i class="fas fa-layer-group"></i></span>
    <h3 class="text-xl font-black text-gray-100 mt-4">{{ t('Create your first project') }}</h3>
    <p class="text-sm text-gray-400 mt-2 leading-relaxed">{{ t('A project is a channel, a client or a business: each has its own videos, hooks, creators and sources. Later you can create more and also see them all together.') }}<template v-if="has">{{ ' ' }}<b class="text-gray-200">{{ t('Everything you already have will go into this project.') }}</b></template></p>
    <label class="block text-xs font-bold text-gray-400 mt-5 mb-2 tracking-wider">{{ t('PROJECT NAME') }}</label>
    <input id="firstProjectName" ref="nameInput" v-model="name" :maxlength="PROJECT_NAME_MAX" :placeholder="t('E.g. YouTube channel')" :class="inputClass" @keydown.enter="create">
    <label class="block text-xs font-bold text-gray-400 mt-4 mb-2 tracking-wider">{{ t('COLOUR') }}</label>
    <ColorSwatches id="firstProjectColors" v-model="color" class="flex flex-wrap gap-2" />
    <button type="button" class="mt-6 w-full bg-brand hover:bg-brandDark text-white py-3 rounded-xl font-bold" @click="create"><i class="fas fa-check mr-1.5"></i>{{ t('Create project') }}</button>
  </CardDialog>
</template>
